import { type ComponentProps, Fragment, useMemo, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { tv } from "tailwind-variants";

const sliderVariants = tv({
  slots: {
    root: "relative flex w-full touch-none items-center select-none data-disabled:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
    track:
      "relative grow overflow-hidden rounded-full bg-surface-active data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
    range:
      "absolute bg-accent data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full",
    thumb:
      "block size-4 shrink-0 rounded-full border border-accent bg-surface shadow-sm ring-focus-ring/30 transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
  },
});

const { range, root, thumb, track } = sliderVariants();

type SliderProps = ComponentProps<typeof SliderPrimitive.Root> & {
  formatLabel?: (value: number) => string;
};

export const Slider = ({
  className,
  defaultValue,
  formatLabel,
  max = 100,
  min = 0,
  onValueChange,
  value,
  ...props
}: SliderProps) => {
  const _values = useMemo(() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (Array.isArray(defaultValue)) {
      return defaultValue;
    }

    return [min, max];
  }, [value, defaultValue, min, max]);

  const [currentValues, setCurrentValues] = useState<number[]>(() => {
    if (Array.isArray(value)) {
      return value;
    }
    if (Array.isArray(defaultValue)) {
      return defaultValue;
    }

    return [min, max];
  });

  return (
    <SliderPrimitive.Root
      className={root({
        className: [formatLabel !== undefined && "pb-6", className].filter(Boolean).join(" "),
      })}
      data-slot="slider"
      defaultValue={defaultValue}
      max={max}
      min={min}
      onValueChange={(vals) => {
        setCurrentValues(vals);
        onValueChange?.(vals);
      }}
      value={value}
      {...props}
    >
      <SliderPrimitive.Track className={track()} data-slot="slider-track">
        <SliderPrimitive.Range className={range()} data-slot="slider-range" />
      </SliderPrimitive.Track>

      {Array.from({ length: _values.length }, (_, index) => {
        const val = currentValues[index] ?? _values[index];
        const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;

        return (
          <Fragment key={index}>
            {formatLabel !== undefined && (
              <span
                className="pointer-events-none absolute top-1/2 text-xs whitespace-nowrap text-content-muted"
                style={{ left: `${pct}%`, transform: `translateX(-${pct}%) translateY(0.5rem)` }}
                aria-hidden
              >
                {formatLabel(val)}
              </span>
            )}
            <SliderPrimitive.Thumb className={thumb()} data-slot="slider-thumb" />
          </Fragment>
        );
      })}
    </SliderPrimitive.Root>
  );
};
